import * as LucideIcons from 'lucide-react';
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import './ToggleButton.css';

interface ToggleState {
  label: string;
  icon?: {
    kind: string;
    value: string;
  };
  target_type?: string;
  target_id?: string;
  data_api?: string;
  button_text?: string;
  button_action?: {
    url?: string;
    method?: string;
    data?: any;
  };
}

interface ToggleButtonBlock {
  default_state?: string;
  state_one: ToggleState;
  state_two: ToggleState;
  size?: 'small' | 'medium' | 'large';
  is_draggable?: boolean;
  disabled?: boolean;
}

interface ToggleButtonProps {
  block: ToggleButtonBlock;
}

const ToggleButton: React.FC<ToggleButtonProps> = ({ block }) => {
  const [activeState, setActiveState] = useState<string>(
    block.default_state || 'one'
  );

  if (!block.state_one || !block.state_two) {
    console.warn('ToggleButton requires both state_one and state_two');
    return null;
  }

  const currentState =
    activeState === 'one' ? block.state_one : block.state_two;
  const otherState = activeState === 'one' ? block.state_two : block.state_one;

  const handleToggle = () => {
    const newState = activeState === 'one' ? 'two' : 'one';
    setActiveState(newState);

    // Find and update the target data table
    if (currentState.target_type === 'data_table' && currentState.target_id) {
      const targetElement = document.getElementById(currentState.target_id);
      if (targetElement) {
        // Trigger a data refresh on the target table
        const event = new CustomEvent('toggleButtonStateChange', {
          detail: {
            newState,
            newApiEndpoint:
              newState === 'one'
                ? block.state_one.data_api
                : block.state_two.data_api,
            targetId: currentState.target_id,
          },
        });
        targetElement.dispatchEvent(event);
      }
    }
  };

  const renderIcon = (icon: ToggleState['icon']) => {
    if (!icon) return null;

    if (icon.kind === 'lucide') {
      const IconComponent = LucideIcons[icon.value];
      return IconComponent ? (
        <IconComponent size={16} className="me-1" />
      ) : null;
    }

    return <img src={icon.value} alt="" className="toggle-icon me-1" />;
  };

  const getSizeClass = (): string => {
    switch (block.size) {
      case 'small':
        return 'toggle-sm';
      case 'large':
        return 'toggle-lg';
      case 'medium':
      default:
        return '';
    }
  };

  return (
    <div
      className={`toggle-button-container ${block.is_draggable ? 'draggable' : ''}`}
    >
      <div className={`toggle-with-labels ${getSizeClass()}`}>
        {/* Combined labels on the left */}
        <div className="toggle-labels-group">
          <div
            className={`toggle-external-label ${activeState === 'one' ? 'active' : ''}`}
          >
            {renderIcon(block.state_one.icon)}
            <span>{block.state_one.label}</span>
          </div>

          <span className="toggle-separator">/</span>

          <div
            className={`toggle-external-label ${activeState === 'two' ? 'active' : ''}`}
          >
            {renderIcon(block.state_two.icon)}
            <span>{block.state_two.label}</span>
          </div>
        </div>

        {/* Toggle Switch on the right */}
        <div
          className={`toggle-switch ${getSizeClass()} ${block.disabled ? 'disabled' : ''}`}
          onClick={!block.disabled ? handleToggle : undefined}
        >
          <div
            className={`toggle-circle ${activeState === 'two' ? 'right' : 'left'}`}
          ></div>
        </div>
      </div>

      {/* Optional button action for current state */}
      {currentState.button_text && currentState.button_action && (
        <Button
          variant="secondary"
          onClick={() => {
            if (currentState.button_action.url) {
              if (currentState.button_action.method === 'GET') {
                window.open(currentState.button_action.url, '_blank');
              } else {
                // Handle POST/PUT/DELETE actions
                fetch(currentState.button_action.url, {
                  method: currentState.button_action.method,
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: currentState.button_action.data
                    ? JSON.stringify(currentState.button_action.data)
                    : undefined,
                }).catch(error =>
                  console.error('Button action failed:', error)
                );
              }
            }
          }}
          className={`ms-2 ${getSizeClass()}`}
          disabled={block.disabled}
        >
          {currentState.button_text}
        </Button>
      )}
    </div>
  );
};

export default ToggleButton;
